<template>
  <div>
    <v-dialog v-model="previewDialog" width="750">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          {{ $t("documentation.editor.dialog.title") }}
        </v-card-title>

        <div class="tw-m-6" v-html="parsed"></div>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" small text @click="previewDialog = false">
            {{ $t("common.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row
      align="center"
      class="tw-border tw-rounded elevation-1 tw-bg-white"
      style="margin-left: 0;margin-right: 0;margin-bottom: 6px;"
    >
      <v-col cols="6">
        <v-text-field
          v-model="documentation.path"
          :label="$t('documentation.editor.form.path')"
          class="tw-mb-2"
        ></v-text-field>
        <LocaleField v-model="locale" :props="{ hideDetails: true }"></LocaleField>
      </v-col>
      <v-col cols="3">
        <v-btn block class="tw-mt-2" color="primary" depressed small @click="reset">
          {{ $t("documentation.editor.reset") }}
        </v-btn>
        <v-btn block class="tw-mt-2" color="error" depressed small @click="remove">
          {{ $t("documentation.editor.remove") }}
        </v-btn>
      </v-col>
      <v-col cols="3">
        <v-btn block class="tw-mt-2" color="primary" depressed small @click="preview">
          {{ $t("common.preview") }}
        </v-btn>
        <v-btn block class="tw-mt-2" color="green lighten-1 white--text" depressed small @click="save">
          {{ $t("common.save") }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Markdown editor -->
    <div class="sr-editor elevation-2">
      <div class="sr-editor-toolbar">
        <v-icon
          :title="$t('documentation.editor.toolbar.bold')"
          class="sr-editor-button"
          small
          @click="editorAction('bold')"
        >
          fa-bold
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.italic')"
          class="sr-editor-button"
          small
          @click="editorAction('italic')"
        >
          fa-italic
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.strikethrough')"
          class="sr-editor-button"
          small
          @click="editorAction('strikethrough')"
        >
          fa-strikethrough
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.quote')"
          class="sr-editor-button"
          small
          @click="editorAction('quote')"
        >
          fa-quote-left
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.heading')"
          class="sr-editor-button"
          small
          @click="editorAction('heading')"
        >
          fa-heading
        </v-icon>
        <i class="sr-editor-spacer"></i>
        <v-icon
          :title="$t('documentation.editor.toolbar.ul')"
          class="sr-editor-button"
          small
          @click="editorAction('ul')"
        >
          fa-list-ul
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.ol')"
          class="sr-editor-button"
          small
          @click="editorAction('ol')"
        >
          fa-list-ol
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.table')"
          class="sr-editor-button"
          small
          @click="editorAction('table')"
        >
          fa-table
        </v-icon>
        <i class="sr-editor-spacer"></i>
        <v-icon
          :title="$t('documentation.editor.toolbar.code')"
          class="sr-editor-button"
          small
          @click="editorAction('code')"
        >
          fa-code
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.link')"
          class="sr-editor-button"
          small
          @click="editorAction('link')"
        >
          fa-link
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.image')"
          class="sr-editor-button"
          small
          @click="editorAction('image')"
        >
          fa-image
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.youtube')"
          class="sr-editor-button"
          small
          @click="editorAction('youtube')"
        >
          fab fa-youtube
        </v-icon>
        <i class="sr-editor-spacer"></i>
        <v-icon
          :title="$t('documentation.editor.toolbar.icon')"
          class="sr-editor-button"
          small
          @click="editorAction('icon')"
        >
          fas fa-icons
        </v-icon>
        <i class="sr-editor-spacer"></i>
        <v-icon
          :title="$t('documentation.editor.toolbar.comment')"
          class="sr-editor-button"
          small
          @click="editorAction('comment')"
        >
          fa-comment
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.preview')"
          class="sr-editor-button"
          small
          @click="editorAction('preview')"
        >
          fa-eye
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.fullscreen')"
          class="sr-editor-button"
          small
          @click="editorAction('fullscreen')"
        >
          {{ fullscreenIcon }}
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.clear')"
          class="sr-editor-button"
          small
          @click="editorAction('clear')"
        >
          fa-eraser
        </v-icon>
        <v-icon
          :title="$t('documentation.editor.toolbar.help')"
          class="sr-editor-button"
          small
          @click="editorAction('help')"
        >
          fa-question-circle
        </v-icon>
      </div>
      <textarea ref="editor" v-model="content" class="sr-editor-content"></textarea>
    </div>
  </div>
</template>

<script>
import LocaleField from "../../Form/LocaleField"
import { isset } from "../../../../helpers"
import { Types as TypesSnackbar } from "../../../../store/modules/snackbar"
import parser from "../../../../mixins/parser"

export default {
  name: "Editor",
  components: {
    LocaleField,
  },
  mixins: [parser],
  props: {
    selected: { type: [String], default: null },
  },
  data: () => ({
    previewDialog: false,
    fullscreen: false,
    content: "",
    locale: "fr",
    documentation: {
      path: "",
    },
  }),
  watch: {
    content(value) {
      this.parse(value)
    },
    selected: function(value) {
      this.fetchDocumentation(value)
    },
    locale: function(value) {
      if (!value) {
        return
      }

      const key = `content_${value.toLowerCase()}`

      if (this.documentation[key] !== undefined) {
        this.content = isset(this.documentation[key]) ? this.documentation[key] : ""
      }
    },
  },
  mounted() {
    this.fetchDocumentation(this.selected)
  },
  computed: {
    fullscreenIcon() {
      return this.fullscreen ? "fa-compress-arrows-alt" : "fa-expand-arrows-alt"
    },
  },
  methods: {
    fetchDocumentation(value) {
      if (value !== null) {
        this.$axios
          .get("office/common/documentations/by?path=" + encodeURIComponent(value))
          .then(response => {
            this.documentation = response.data.data
            this.content = this.documentation[`content_${this.locale}`]
          })
          .catch(() => {
            this.reset()
            this.documentation.path = value
          })
      } else {
        this.reset()
      }
    },
    reset() {
      this.documentation = {
        path: "",
      }

      this.content = ""
    },
    remove() {
      if (confirm("Attention! Voulez vous réellement supprimer cette documentation ? ")) {
        this.$axios.delete("office/common/documentations/by?path=" + this.documentation.path).then(() => {
          window.location.reload()
        })
      }
    },
    preview() {
      this.parse(this.content)
      this.previewDialog = true
    },
    save() {
      this.parse(this.content)

      let params = this.documentation

      params[`content_${this.locale}`] = this.raw

      this.$axios
        .post("office/common/documentations", params)
        .then(response => {
          this.documentation = response.data.data
          this.$store.dispatch(TypesSnackbar.actions.PUSH_MESSAGE, {
            lines: this.$t("documentation.editor.save.success"),
            config: { timeout: 1000 * 4, color: "success" },
          })
        })
        .catch(() => {
          this.$store.dispatch(TypesSnackbar.actions.PUSH_MESSAGE, {
            lines: this.$t("documentation.editor.save.error"),
            config: { timeout: 1000 * 4, color: "error" },
          })
        })
        .finally(() => {
          this.$emit("reload")
        })
    },
    editorAction(action) {
      switch (action) {
        case "bold":
          this.insert("****")
          break
        case "italic":
          this.insert("**")
          break
        case "strikethrough":
          this.insert("~~~~")
          break
        case "quote":
          this.insert("> ")
          break
        case "heading":
          this.insert("# ")
          break
        case "ul":
          this.insert("* ")
          break
        case "ol":
          this.insert("1. ")
          break
        case "table":
          this.insert(
            "| Column | Column | Column |\n" +
              "|--------|:--------:|--------:|\n" +
              "| Cell | Cell | Cell |\n" +
              "| Cell | Cell | Cell |"
          )
          break
        case "link":
          this.insert("[]()")
          break
        case "image":
          this.insert("![]()")
          break
        case "youtube":
          this.insert("@[youtube]()")
          break
        case "icon":
          this.insert("@[icon]()")
          break
        case "code":
          this.insert("```\n\n```")
          break
        case "comment":
          this.insert("```comments\n\n```")
          break
        case "preview":
          this.preview()
          break
        case "fullscreen":
          // If there's an element in fullscreen, exit
          // Otherwise, enter it
          if (this.fullscreen) {
            document.exitFullscreen()
            this.fullscreen = false
          } else {
            document.documentElement.requestFullscreen()
            this.fullscreen = true
          }
          break
        case "clear":
          this.content = ""
          break
        case "help":
          window.open("https://simplemde.com/markdown-guide")
          break
        default:
          this.insert("\n")
          break
      }
      this.$refs.editor.focus()
    },
    insert(text) {
      document.execCommand("insertText", false, text)
    },
  },
}
</script>
